import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Movie from "../components/Movie";
import MovieSkeleton from "../components/MovieSkeleton";
import { getFavorites, toggleFavorite } from "../utils/favorites";

const FALLBACK_POSTER = "/fallback-poster.svg";

// 외부 이미지(http) URL이 들어오는 경우 https로 정규화하고,
// 값이 없으면 로컬 fallback 이미지를 사용한다.
const normalizeImageUrl = (url) => {
  if (!url) return FALLBACK_POSTER;
  return url.replace(/^http:\/\//i, "https://");
};

function Detail() {
  // URL 파라미터 /detail/:id 에서 movie id를 가져온다.
  const { id } = useParams();
  // 상세 화면 데이터 상태
  const [loading, setLoading] = useState(true);
  const [movie, setMovie] = useState(null);
  const [cast, setCast] = useState([]);
  const [videos, setVideos] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [error, setError] = useState("");
  // 재시도 버튼 트리거
  const [refreshKey, setRefreshKey] = useState(0);
  // 즐겨찾기 동기화 상태
  const [favoriteIds, setFavoriteIds] = useState([]);

  // localStorage에서 즐겨찾기 id만 추출해 빠르게 포함 여부를 검사한다.
  const loadFavoriteIds = () => setFavoriteIds(getFavorites().map((item) => item.id));

  useEffect(() => {
    // 다른 화면에서 즐겨찾기가 바뀌면 이 상세 화면도 즉시 반영한다.
    loadFavoriteIds();
    const onFavoritesUpdated = () => loadFavoriteIds();
    window.addEventListener("favorites-updated", onFavoritesUpdated);
    return () => window.removeEventListener("favorites-updated", onFavoritesUpdated);
  }, []);

  useEffect(() => {
    // id 변경(다른 영화 이동) 또는 재시도 시 상세 데이터를 재조회한다.
    const controller = new AbortController();
    const getMovie = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/movie/${id}`, { signal: controller.signal });

        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || `Request failed with status ${response.status}`);
        }

        const data = await response.json();
        // 백엔드가 내려주는 상세 응답 구조를 화면별 상태로 분해해 저장한다.
        setMovie(data.data.movie || null);
        setCast(data.data.cast || []);
        setVideos(data.data.videos || []);
        setRecommendations(data.data.recommendations || []);
        setError("");
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching movie:", err);
        setError(err.message || "Failed to fetch movie.");
      } finally {
        setLoading(false);
      }
    };

    getMovie();
    return () => controller.abort();
  }, [id, refreshKey]);

  // 실패한 동일 요청을 다시 실행하기 위한 키 갱신
  const onRetry = () => setRefreshKey((prev) => prev + 1);

  // 현재 상세 영화 또는 추천 영화의 즐겨찾기 토글을 처리한다.
  const onToggleFavorite = (movieId) => {
    if (!movie) return;
    if (movie.id === movieId) {
      toggleFavorite({
        id: movie.id,
        title: movie.title,
        summary: movie.summary,
        genres: movie.genres,
        rating: movie.rating,
        year: movie.year,
        medium_cover_image: movie.medium_cover_image,
        large_cover_image: movie.large_cover_image,
      });
    } else {
      const found = recommendations.find((item) => item.id === movieId);
      if (!found) return;
      toggleFavorite({
        id: found.id,
        title: found.title,
        summary: found.summary,
        genres: found.genres,
        rating: found.rating,
        year: found.year,
        medium_cover_image: found.medium_cover_image,
        large_cover_image: found.large_cover_image,
      });
    }
    loadFavoriteIds();
  };

  // 실제 외부 링크가 있는 YouTube 영상만 UI에 노출한다.
  const youtubeVideos = useMemo(() => videos.filter((video) => video.url), [videos]);

  // 로딩 상태 UI
  if (loading) {
    return (
      <div className="page">
        <header className="topbar">
          <h1>Movie Details</h1>
          <Link className="link-btn" to="/">
            Back to Home
          </Link>
        </header>
        <div className="movie-grid">
          <MovieSkeleton />
          <MovieSkeleton />
        </div>
      </div>
    );
  }

  // 오류 또는 데이터 없음 상태 UI
  if (error || !movie) {
    return (
      <div className="page">
        <section className="state-box">
          <h2>Failed to load movie</h2>
          <p>{error || "Movie not found."}</p>
          <button type="button" onClick={onRetry}>
            Retry
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="topbar">
        <h1>{movie.title}</h1>
        <div className="nav-actions">
          <Link className="link-btn" to="/">
            Home
          </Link>
          <Link className="link-btn" to="/favorites">
            Favorites
          </Link>
        </div>
      </header>

      <section className="detail-hero">
        {/* 포스터 실패 시 fallback으로 강제 교체 */}
        <img
          className="detail-poster"
          src={normalizeImageUrl(movie.large_cover_image)}
          alt={movie.title}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = FALLBACK_POSTER;
          }}
        />
        <div className="detail-info">
          <p>
            <strong>Year:</strong> {movie.year || "N/A"}
          </p>
          <p>
            <strong>Rating:</strong> {Number(movie.rating || 0).toFixed(1)}
          </p>
          <p>
            <strong>Runtime:</strong> {movie.runtime ? `${movie.runtime} min` : "N/A"}
          </p>
          <button
            type="button"
            className={`fav-btn ${favoriteIds.includes(movie.id) ? "active" : ""}`}
            onClick={() => onToggleFavorite(movie.id)}
          >
            {favoriteIds.includes(movie.id) ? "★ Remove Favorite" : "☆ Add Favorite"}
          </button>
          <p>{movie.summary}</p>
          <ul className="genre-list">
            {(movie.genres || []).map((genre) => (
              <li key={genre}>{genre}</li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h2>Cast</h2>
        {cast.length === 0 ? (
          <p>No cast information available.</p>
        ) : (
          <div className="cast-grid">
            {cast.map((member) => (
              <div className="cast-card" key={member.id}>
                <img
                  src={normalizeImageUrl(member.profile_image)}
                  alt={member.name}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = FALLBACK_POSTER;
                  }}
                />
                <h4>{member.name}</h4>
                <p>{member.character || "-"}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2>Trailers</h2>
        {/* 외부 YouTube 링크는 새 탭으로 연다. */}
        {youtubeVideos.length === 0 ? (
          <p>No trailers available.</p>
        ) : (
          <div className="video-grid">
            {youtubeVideos.map((video) => (
              <a key={video.id} href={video.url} target="_blank" rel="noreferrer" className="video-card">
                <strong>{video.type}</strong>
                <span>{video.name}</span>
              </a>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2>Recommendations</h2>
        {recommendations.length === 0 ? (
          <p>No recommendations available.</p>
        ) : (
          <div className="movie-grid">
            {recommendations.map((item) => (
              <Movie
                key={item.id}
                id={item.id}
                coverImg={item.medium_cover_image}
                title={item.title}
                summary={item.summary}
                genres={item.genres}
                rating={item.rating}
                year={item.year}
                isFavorite={favoriteIds.includes(item.id)}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default Detail;
